import { useState, useEffect } from 'react';
import { BarChart3, Search, FileText, Upload, Download, MapPin, DollarSign, TrendingUp, Plus, Send, Bot, Zap, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import toast from 'react-hot-toast';

const LegionValuation = () => {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const [lvMessages, setLvMessages] = useState([]);
  const [lvInput, setLvInput] = useState('');
  const [conversationId, setConversationId] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/comps/reports`);
      if (res.ok) {
        const data = await res.json();
        setReports(data);
      }
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    }
  };

  const runValuation = async (propertyId) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/comps/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: propertyId,
          num_comps: 6,
          comp_type: 'sale'
        })
      });
      
      if (res.ok) {
        const report = await res.json();
        toast.success('Valuation analysis complete!');
        setSelectedReport(report);
        fetchReports();
      }
    } catch (err) {
      toast.error('Failed to run valuation analysis');
    }
    setLoading(false);
  };

  const sendLvMessage = async () => {
    if (!lvInput.trim()) return;
    
    // Add user message
    const userMsg = { role: 'user', content: lvInput };
    setLvMessages(prev => [...prev, userMsg]);
    setLvInput('');
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/comps/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: lvInput,
          conversation_id: conversationId
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setConversationId(data.conversation_id);
        
        // Add AI response
        const aiMsg = { 
          role: 'assistant', 
          content: data.response,
          arv: data.arv,
          lrv: data.lrv,
          report_id: data.comps_report_id
        };
        setLvMessages(prev => [...prev, aiMsg]);
      }
    } catch (err) {
      toast.error('LV assistant error');
    }
  };

  const downloadPDF = async (reportId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/comps/pdf/${reportId}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `legion_valuation_report_${reportId}.pdf`;
        a.click();
        toast.success('PDF downloaded!');
      }
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                Legion Valuation
                <Badge className="bg-gradient-to-r from-blue-500 to-green-500 text-white border-0">LV</Badge>
              </h1>
              <p className="text-muted-foreground mt-1">
                Instant valuation for land, residential properties, multifamily, and development parcels — powered by Legion AI.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <Tabs defaultValue="lv-assistant" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="lv-assistant">Legion Valuation Assistant</TabsTrigger>
            <TabsTrigger value="manual">Manual Valuation</TabsTrigger>
            <TabsTrigger value="reports">Valuation Reports ({reports.length})</TabsTrigger>
          </TabsList>

          {/* Legion Valuation Assistant */}
          <TabsContent value="lv-assistant">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-8">
                <Card className="h-[600px] flex flex-col border-2">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-950/20 dark:to-green-950/20">
                    <CardTitle className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-white" />
                      </div>
                      Legion Valuation Assistant
                    </CardTitle>
                    <CardDescription>
                      Paste an MLS link, upload a comps CSV, or describe a property or land parcel. 
                      LV will analyze comps, calculate value ranges, and generate offer recommendations.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto space-y-4 mb-4 p-4">
                      {lvMessages.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                          <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-500 to-green-500 mx-auto mb-4 flex items-center justify-center">
                            <BarChart3 className="h-10 w-10 text-white" />
                          </div>
                          <p className="text-lg font-medium mb-2">Welcome to Legion Valuation!</p>
                          <p className="text-sm">Paste an MLS link, upload comps CSV, or describe your property.</p>
                          <p className="text-sm mt-1">I'll analyze it and give you valuation ranges, comps, and offer recommendations.</p>
                        </div>
                      ) : (
                        lvMessages.map((msg, idx) => (
                          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] rounded-lg p-4 ${
                              msg.role === 'user' 
                                ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white' 
                                : 'bg-secondary'
                            }`}>
                              <p className="text-sm">{msg.content}</p>
                              {msg.arv && (
                                <div className="mt-3 pt-3 border-t border-white/20">
                                  <div className="text-xs font-semibold mb-1">Estimated ARV:</div>
                                  <div className="text-2xl font-bold">${msg.arv.toLocaleString()}</div>
                                </div>
                              )}
                              {msg.lrv && (
                                <div className="mt-3 pt-3 border-t border-white/20">
                                  <div className="text-xs font-semibold mb-1">Land Residual Value (LRV):</div>
                                  <div className="text-2xl font-bold">${msg.lrv.toLocaleString()}</div>
                                </div>
                              )}
                              {msg.report_id && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="mt-2"
                                  onClick={() => downloadPDF(msg.report_id)}
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  Download PDF Report
                                </Button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Input */}
                    <div className="flex gap-2 p-4 border-t">
                      <Input 
                        placeholder="Paste MLS link, describe property, or ask for valuation..."
                        value={lvInput}
                        onChange={(e) => setLvInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendLvMessage()}
                      />
                      <Button onClick={sendLvMessage} className="bg-gradient-to-r from-blue-500 to-green-500">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="col-span-4 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button variant="outline" className="w-full justify-start">
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Valuation CSV
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <FileText className="h-4 w-4 mr-2" />
                      Paste MLS Data
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Download className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sample Questions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <button 
                      className="text-left w-full p-2 rounded hover:bg-secondary transition-colors"
                      onClick={() => setLvInput("What's the valuation for 123 Main St, Irvine CA?")}
                    >
                      "What's the valuation for [address]?"
                    </button>
                    <button 
                      className="text-left w-full p-2 rounded hover:bg-secondary transition-colors"
                      onClick={() => setLvInput("Analyze this land parcel: 1.5 acres, utilities available")}
                    >
                      "Analyze this land parcel..."
                    </button>
                    <button 
                      className="text-left w-full p-2 rounded hover:bg-secondary transition-colors"
                      onClick={() => setLvInput("Generate PDF valuation report")}
                    >
                      "Generate PDF valuation report"
                    </button>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-950/20 dark:to-green-950/20">
                  <CardContent className="pt-6">
                    <div className="text-xs text-muted-foreground mb-2">Powered by</div>
                    <div className="flex items-center gap-2">
                      <Bot className="h-5 w-5 text-primary" />
                      <span className="font-bold text-lg">Legion AI</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Manual Valuation */}
          <TabsContent value="manual">
            <Card>
              <CardHeader>
                <CardTitle>Manual Valuation Analysis</CardTitle>
                <CardDescription>Search and analyze comparable properties for any asset class</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-2">
                  <Input 
                    placeholder="Enter address, APN, or parcel number..."
                    value={searchAddress}
                    onChange={(e) => setSearchAddress(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={() => toast.info('Search integration pending')} className="bg-gradient-to-r from-blue-500 to-green-500">
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <Card className="border-2">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">Low Value</div>
                        <div className="text-3xl font-bold text-red-500">$0</div>
                        <div className="text-xs text-muted-foreground mt-1">Conservative estimate</div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-2 border-primary bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-950/20 dark:to-green-950/20">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">Median Valuation</div>
                        <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">$0</div>
                        <div className="text-xs text-muted-foreground mt-1">ARV / LRV</div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-2">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">High Value</div>
                        <div className="text-3xl font-bold text-green-500">$0</div>
                        <div className="text-xs text-muted-foreground mt-1">Optimistic estimate</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="text-center py-12 text-muted-foreground">
                  <Search className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p>Search for a property to begin valuation analysis</p>
                  <p className="text-sm mt-2">Supports: Land, SFR, Multifamily, Commercial</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Valuation Reports */}
          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Valuation Reports</CardTitle>
                    <CardDescription>View and download all generated valuation reports</CardDescription>
                  </div>
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Export All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {reports.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-16 w-16 mx-auto mb-4 opacity-20" />
                    <p>No valuation reports yet</p>
                    <p className="text-sm mt-2">Reports will appear here after running valuation analysis or using Legion Valuation Assistant</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reports.map(report => (
                      <div 
                        key={report.id}
                        className="border rounded-lg p-4 hover:bg-secondary/50 cursor-pointer transition-colors"
                        onClick={() => setSelectedReport(report)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold">{report.subject_address}</div>
                            <div className="text-sm text-muted-foreground">
                              {report.property_type} • {report.comps?.length || 0} comps analyzed
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                              ${report.arv?.toLocaleString() || '0'}
                            </div>
                            <div className="text-xs text-muted-foreground">Valuation</div>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Badge variant="outline">
                            Offer Range: ${report.offer_range_low?.toLocaleString()} - ${report.offer_range_high?.toLocaleString()}
                          </Badge>
                          <Badge className="bg-gradient-to-r from-blue-500 to-green-500 text-white border-0">✓ {report.status}</Badge>
                          {report.generated_by === 'bot' && (
                            <Badge variant="secondary">
                              <Bot className="h-3 w-3 mr-1" />
                              Bot Generated
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Report Details Modal */}
      {selectedReport && (
        <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Legion Valuation Report
                <Badge className="bg-gradient-to-r from-blue-500 to-green-500 text-white border-0">LV</Badge>
              </DialogTitle>
              <DialogDescription>
                {selectedReport.subject_address} • {selectedReport.property_type} • Generated {new Date(selectedReport.created_at).toLocaleDateString()}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Valuation Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-muted-foreground mb-1">Low Value</div>
                    <div className="text-2xl font-bold">${selectedReport.low_value?.toLocaleString()}</div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-primary bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-950/20 dark:to-green-950/20">
                  <CardContent className="pt-6">
                    <div className="text-sm text-muted-foreground mb-1">Median Valuation</div>
                    <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">${selectedReport.arv?.toLocaleString()}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-muted-foreground mb-1">High Value</div>
                    <div className="text-2xl font-bold">${selectedReport.high_value?.toLocaleString()}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Offer Range */}
              <Card className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-950/20 dark:to-blue-950/20 border-2">
                <CardContent className="pt-6">
                  <div className="text-sm font-medium mb-2">Legion Recommended Offer Range</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-muted-foreground">Low Offer (65%)</div>
                      <div className="text-xl font-bold">${selectedReport.offer_range_low?.toLocaleString()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">Recommended (75%)</div>
                      <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">${selectedReport.recommended_offer?.toLocaleString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">High Offer (85%)</div>
                      <div className="text-xl font-bold">${selectedReport.offer_range_high?.toLocaleString()}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Comps Table */}
              <div>
                <div className="text-sm font-medium mb-3">Comparable Properties ({selectedReport.comps?.length || 0})</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b">
                      <tr className="text-left text-muted-foreground">
                        <th className="pb-2">Address</th>
                        <th className="pb-2">Sale Price</th>
                        <th className="pb-2">Distance</th>
                        <th className="pb-2">Similarity</th>
                        <th className="pb-2">Adjusted Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedReport.comps?.slice(0, 6).map((comp, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="py-3">{comp.address}</td>
                          <td className="font-mono">${comp.sale_price?.toLocaleString()}</td>
                          <td>{comp.distance_miles?.toFixed(1)} mi</td>
                          <td>{(comp.similarity_score * 100)?.toFixed(0)}%</td>
                          <td className="font-mono font-bold">${comp.adjusted_value?.toLocaleString() || comp.sale_price?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={() => downloadPDF(selectedReport.id)} className="bg-gradient-to-r from-blue-500 to-green-500">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF Report
                </Button>
                <Button variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Deal Pipeline
                </Button>
                <Button variant="outline">
                  <Send className="h-4 w-4 mr-2" />
                  Send to Seller
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default LegionValuation;